import React from "react";
import { MdDoneAll } from "react-icons/md";

import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import SheetDialog from "./SheetDialog";
import { FULFILLED_OUTSIDE_LABEL } from "./runs";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Complete run / Close run, from the route sheet: the stops still to do are
 * recorded from their orders and the run closes. It asks first, with what
 * each stop becomes: `fulfilled` (their orders are fulfilled, but not
 * through the delivery list: "Fulfilled outside this run") and
 * `notDelivered` (not fulfilled, Attention included; only Close run has
 * those). Nothing becomes delivered or partly delivered: only the delivery
 * list records those. `onConfirm()` closes it; `error` is why the API
 * refused.
 */
const CompleteRunDialog = ({
  runName,
  fulfilled = 0,
  notDelivered = 0,
  busy = false,
  error = "",
  onConfirm,
  onClose,
}) => {
  const count = fulfilled + notDelivered;
  const allFulfilled = fulfilled === count;
  return (
    <SheetDialog
      open
      labelId="completeRunTitle"
      title={allFulfilled ? "Complete this run?" : "Close this run?"}
      subtitle={runName}
      icon={MdDoneAll}
      closeDisabled={busy}
      onClose={onClose}
      footer={
        <>
          <BoltButton
            type="button"
            variant="outline"
            size="sm"
            label="Keep it open"
            onClick={onClose}
            disabled={busy}
          />
          <BoltButton
            type="button"
            size="sm"
            label={
              busy ? "Closing…" : allFulfilled ? "Complete run" : "Close run"
            }
            onClick={onConfirm}
            disabled={busy}
          />
        </>
      }
    >
      {allFulfilled ? (
        <p className="m-[0px] text-[12.5px] text-ui-text">
          Every order still to do on this run is fulfilled already, from the
          order page. Completing it records{" "}
          {count === 1 ? "that stop" : `those ${plural(count, "stop")}`} as{" "}
          <b>{FULFILLED_OUTSIDE_LABEL.toLowerCase()}</b>, at the time each order
          was fulfilled, and closes the run.
        </p>
      ) : (
        <>
          <p className="m-[0px] text-[12.5px] text-ui-text">
            {count === 1
              ? "The stop still to do is recorded from its order"
              : `The ${plural(count, "stop")} still to do are recorded from their orders`}
            , and the run closes:
          </p>
          <ul className="m-[0px] grid list-disc gap-[4px] pl-[18px] text-[12.5px] text-ui-text">
            {fulfilled > 0 && (
              <li>
                <b>
                  {fulfilled} {FULFILLED_OUTSIDE_LABEL.toLowerCase()}
                </b>
                : {fulfilled === 1 ? "its order is" : "their orders are"}{" "}
                fulfilled
              </li>
            )}
            {notDelivered > 0 && (
              <li>
                <b>{notDelivered} not delivered</b>: not fulfilled yet, so{" "}
                {notDelivered === 1 ? "it waits" : "they wait"} for another run
              </li>
            )}
          </ul>
        </>
      )}
      <p className="m-[0px] text-[12px] text-ui-text-muted">
        Nothing is recorded as delivered: only the delivery list does that.
        Customers aren't emailed.
      </p>
      {error && (
        <p
          role="alert"
          className="m-[0px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[8px] text-[12px] text-state-danger-text"
        >
          {error}
        </p>
      )}
    </SheetDialog>
  );
};

export default CompleteRunDialog;
